import type {
    ComponentProps,
    ComponentSettings,
    ComponentState,
    ComponentStorage,
    EventHandlers,
    IUIManagerConfig,
    VariantItem,
} from '@/Core/Contracts/UI/UIManager'
import { action, makeObservable, observable, runInAction, toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { GetTask } from '../../Tasks/Utils/GetTask'
import { SetTask } from '../../Tasks/Utils/SetTask'
import { EventManager } from '../Event'
import { profilerManager } from '../Profiler/ProfilerManager'

/**
 * UIManager — менеджер для керування UI будь-якого компоненту ядра
 */
export class UIManager {
    /**
     * Реактивні стани компонента
     */
    states: ComponentState = {}

    /**
     * Сховище даних компонента
     */
    storages: ComponentStorage = {}

    /**
     * Налаштування компонента
     */
    settings: ComponentSettings = {}

    /**
     * Шаблон компонента
     */
    template: React.ComponentType<any> | null = null

    /**
     * Обгортка для шаблону
     */
    templateWrapper: React.ComponentType<any> | null = null

    /**
     * Шаблон для помилки 403
     */
    templateError403: React.ComponentType<any> | null = null

    /**
     * Функціональний компонент для виконання скриптів (хуків, ефектів)
     * Рендериться разом з основним шаблоном/враппером при виконанні умов depFn та accessFn
     * 
     * Використовується для виконання side-effects, таких як:
     * - useEffect для підписок на події
     * - useEffect для аналітики
     * - Використання інших хуків, що не впливають на UI
     * 
     * @example
     * ```typescript
     * const uiManager = new UIManager({
     *   template: MyComponent,
     *   templateScripts: () => {
     *     useEffect(() => {
     *       console.log('Component mounted')
     *       return () => console.log('Component unmounted')
     *     }, [])
     *     return null
     *   }
     * })
     * ```
     */
    templateScripts: React.ComponentType<any> | null = null

    /**
     * Функція залежності для умовного рендерингу
     */
    depFn: ((options: Record<string, any>) => boolean) | null = null

    /**
     * Режим доступу до компонента
     * Може бути використаний у accessFn для визначення прав доступу
     * @example
     * ```typescript
     * const uiManager = new UIManager({
     *   accessMode: 'delete',
     *   accessFn: ({ context }) => {
     *     return context.accessMode === 'delete' && user.isAdmin()
     *   }
     * })
     * ```
     */
    accessMode: string | null = null

    /**
     * Об'єкт елемента доступу
     * Може містити будь-які дані для перевірки прав доступу
     * @example
     * ```typescript
     * const uiManager = new UIManager({
     *   accessItem: { role: 'admin', permissions: ['read', 'write'] },
     *   accessFn: ({ accessItem }) => {
     *     return accessItem.permissions.includes('write')
     *   }
     * })
     * ```
     */
    accessItem: Record<string, any> = {}

    /**
     * Функція перевірки доступу
     * За замовчуванням дозволяє доступ (повертає true)
     */
    accessFn: (options: Record<string, any>) => boolean = () => true

    /**
     * Пропси для обгортки шаблону
     */
    templateWrapperProps: ComponentProps = {}

    /**
     * Основні пропси компонента
     */
    props: ComponentProps = {}

    /**
     * Вкладені елементи для рендерингу
     */
    children?: React.ReactNode

    /**
     * Чи потрібно обгортати render у MobX observer
     * Використовується для оптимізації - якщо компонент не потребує реактивності,
     * можна вимкнути спостереження
     */
    isObservable: boolean = false

    /**
     * Поточний варіант відображення (режим)
     * Observable - при зміні автоматично застосовуються налаштування з variantItems
     */
    variant: string | null = null

    /**
     * Доступні варіанти відображення
     * Не observable - статична конфігурація режимів
     */
    variantItems: Record<string, VariantItem> = {}

    /**
     * Лічильник примусових перерендерів
     * Використовується для викликання перерендеру без зміни observable властивостей
     * Public для можливості спостереження через MobX
     */
    forceRenderCounter: number = 0

    /**
     * Менеджер подій
     */
    private eventManager: EventManager

    /**
     * Контекст компонента
     */
    private context: any

    /**
     * Ключ для передачі контексту у пропсах
     * За замовчуванням 'context', можна змінити при необхідності
     */
    private contextKey: string = 'context'

    /**
     * Налаштування профілювання
     */
    private debugConfig?: {
        id: string
        name: string
        type: 'component' | 'template'
        path: string
        parentId?: string
        enabled: boolean
    }

    /**
     * Метрики продуктивності
     */
    private renderStartTime: number = 0

    constructor(config: IUIManagerConfig = {}) {
        /**
         * Ініціалізація MobX
         */
        makeObservable(this, {
            states: observable,
            settings: observable,
            variant: observable,
            forceRenderCounter: observable,
            setStates: action,
            setState: action,
            mergeStates: action,
            clearStates: action,
            setSettings: action,
            setSetting: action,
            mergeSettings: action,
            clearSettings: action,
            setVariant: action,
            forceRerender: action,
        })

        /**
         * Витягуємо системні параметри з config
         */
        const {
            context,
            contextKey,
            isObservable,
            accessMode,
            accessItem,
            accessFn,
            debug,
            states,
            storages,
            settings,
            eventHandlers,
            template,
            templateError403,
            templateWrapper,
            templateScripts,
            templateWrapperProps,
            depFn,
            children,
            variant,
            variantItems,
            props = {},
            ...restConfig
        } = config

        /**
         * Збереження контексту
         */
        this.context = context || this
        this.contextKey = contextKey || 'context'
        
        /**
         * Встановлюємо isObservable з конфігурації
         */
        this.isObservable = typeof isObservable === 'boolean' ? isObservable : false

        /**
         * Встановлюємо accessMode з конфігурації
         */
        if (accessMode) {
            this.accessMode = accessMode
        }

        /**
         * Встановлюємо accessItem з конфігурації
         */
        if (accessItem && typeof accessItem === 'object') {
            this.accessItem = accessItem
        }

        /**
         * Встановлюємо accessFn зі значенням за замовчуванням або з конфігурації
         */
        this.accessFn = accessFn || (() => true)

        /**
         * Ініціалізація EventManager із контекстом
         */
        this.eventManager = new EventManager({
            context: this.context,
            debug: false,
        })

        /**
         * Налаштування профілювання
         */
        if (debug && this.isDevMode()) {
            /** Перетворюємо тип на допустиме значення для debugConfig */
            const debugType = debug.type === 'template' ? 'template' : 'component'

            this.debugConfig = {
                id: debug.id || this.generateId(),
                name: debug.name || 'UIManager',
                type: debugType,
                path: debug.path || (typeof window !== 'undefined' ? window.location.pathname : '/'),
                ...(debug.parentId && { parentId: debug.parentId }),
                enabled: debug.enabled !== false,
            }

            /**
             * Реєстрація у реєстрі профілювання
             */
            if (this.debugConfig.enabled) {
                const debugId = this.debugConfig.id
                const debugName = this.debugConfig.name
                const debugPath = this.debugConfig.path
                const debugParentId = this.debugConfig.parentId

                profilerManager.registerComponent(debugId, this, {
                    name: debugName,
                    type: this.debugConfig.type as any,
                    path: debugPath || window?.location?.pathname || 'unknown',
                    ...(debugParentId && { parentId: debugParentId }),
                })

                /**
                 * Додаткова реєстрація у templates для шаблонів
                 */
                if (this.debugConfig.type === 'template') {
                    profilerManager.registerTemplate({
                        id: debugId,
                        name: debugName,
                        path: debugPath || window?.location?.pathname || 'unknown',
                        props: this.props,
                        /** @todo Визначити слоти */
                        slots: [],
                        /** @todo Визначити залежності */
                        dependencies: [],
                        /** Приблизний розмір властивостей */
                        size: JSON.stringify(this.props).length,
                    })
                }
            }
        }

        /**
         * Застосування конфігурації
         * Передаємо тільки props та restConfig, системні параметри вже оброблені
         */
        this.mergeToInstance({
            ...(states && { states }),
            ...(storages && { storages }),
            ...(settings && { settings }),
            ...(eventHandlers && { eventHandlers }),
            ...(template && { template }),
            ...(templateError403 && { templateError403 }),
            ...(templateWrapper && { templateWrapper }),
            ...(templateScripts && { templateScripts }),
            ...(templateWrapperProps && { templateWrapperProps }),
            ...(depFn && { depFn }),
            ...(children && { children }),
            ...(variant && { variant }),
            ...(variantItems && { variantItems }),
            props: { ...props, ...restConfig },
        })

        /**
         * Ініціалізація варіантів відображення
         */
        if (variantItems) {
            this.variantItems = variantItems
        }

        if (variant) {
            this.setVariant(variant)
        }
    } /**
     * Встановлює всі стани компонента
     */
    setStates(states: ComponentState): void {
        if (typeof states === 'object' && states !== null) {
            runInAction(() => {
                this.states = { ...states }
            })

            /** Логуємо для профайлера */
            this.logDebugEvent('setStates', { count: Object.keys(states).length })
            this.updateDebugInfo()
        }
    }

    /**
     * Встановлює окремий стан за шляхом
     */
    setState(path: string, value: any): void {
        if (typeof path === 'string' && path.length > 0) {
            runInAction(() => {
                SetTask(this.states, path, value)
            })

            /** Логуємо для профайлера */
            this.logDebugEvent('setState', { path, valueType: typeof value })
            this.updateDebugInfo()
        }
    }

    /**
     * Отримує стан за шляхом
     */
    getState(path: string, defaultValue?: any): any {
        return GetTask(this.states, path, defaultValue)
    }

    /**
     * Очищує всі стани
     */
    clearStates(): void {
        runInAction(() => {
            this.states = {}
        })

        this.logDebugEvent('clearStates')
        this.updateDebugInfo()
    }

    /**
     * Встановлює всі налаштування
     */
    setSettings(settings: ComponentSettings): void {
        if (typeof settings === 'object' && settings !== null) {
            runInAction(() => {
                this.settings = { ...settings }
            })

            this.logDebugEvent('setSettings', { count: Object.keys(settings).length })
            this.updateDebugInfo()
        }
    }

    /**
     * Встановлює окреме налаштування за шляхом
     */
    setSetting(path: string, value: any): void {
        if (typeof path === 'string' && path.length > 0) {
            runInAction(() => {
                SetTask(this.settings, path, value)
            })

            this.logDebugEvent('setSetting', { path, valueType: typeof value })
            this.updateDebugInfo()
        }
    }

    /**
     * Отримує налаштування за шляхом
     */
    getSetting(path: string, defaultValue?: any): any {
        return GetTask(this.settings, path, defaultValue)
    }

    /**
     * Очищує всі налаштування
     */
    clearSettings(): void {
        runInAction(() => {
            this.settings = {}
        })

        this.logDebugEvent('clearSettings')
        this.updateDebugInfo()
    }

    /**
     * Встановлює всі сховища (не observable)
     */
    setStorages(storages: ComponentStorage): void {
        if (typeof storages === 'object' && storages !== null) {
            this.storages = { ...storages }
            this.logDebugEvent('setStorages', { count: Object.keys(storages).length })
        }
    }

    /**
     * Встановлює окреме сховище (не observable)
     */
    setStorage(name: string, value: any): void {
        this.storages[name] = value
        this.logDebugEvent('setStorage', { name, valueType: typeof value })
    }

    /**
     * Отримує значення зі сховища
     */
    getStorage(name: string): any {
        return this.storages[name]
    }

    /**
     * Очищує всі сховища (не observable)
     */
    clearStorages(): void {
        this.storages = {}
        this.logDebugEvent('clearStorages')
    }

    /**
     * Встановлює шаблон компонента
     */
    setTemplate(template: React.ComponentType<any> | null): void {
        runInAction(() => {
            this.template = template
        })

        /** Логуємо для профайлера */
        this.logDebugEvent('setTemplate', {
            templateName: template?.name || template?.displayName || 'anonymous',
            hasTemplate: template !== null,
        })
        this.updateDebugInfo()
    }

    /**
     * Встановлює обгортку для шаблону
     */
    setTemplateWrapper(wrapper: React.ComponentType<any> | null): void {
        runInAction(() => {
            this.templateWrapper = wrapper
        })
    }

    /**
     * Встановлює шаблон для помилки 403
     */
    setTemplateError403(template: React.ComponentType<any> | null): void {
        runInAction(() => {
            this.templateError403 = template
        })
    }

    /**
     * Встановлює функціональний компонент для виконання скриптів (хуків, ефектів)
     * 
     * templateScripts буде рендеритись разом з основним компонентом тільки якщо:
     * - accessFn повертає true (доступ дозволено)
     * - depFn повертає true або не встановлений (залежності виконані)
     * - Є основний result (template або children)
     * 
     * @example
     * ```typescript
     * // Простий приклад з useEffect
     * uiManager.setTemplateScripts(() => {
     *   useEffect(() => {
     *     console.log('Component mounted')
     *     return () => console.log('Component unmounted')
     *   }, [])
     *   return null
     * })
     * 
     * // Приклад з підпискою на події
     * uiManager.setTemplateScripts(() => {
     *   const { context } = useContext(MyContext)
     *   
     *   useEffect(() => {
     *     const subscription = context.eventManager.on('dataChanged', (data) => {
     *       console.log('Data changed:', data)
     *     })
     *     
     *     return () => subscription.unsubscribe()
     *   }, [context])
     *   
     *   return null
     * })
     * 
     * // Приклад з аналітикою
     * uiManager.setTemplateScripts(() => {
     *   useEffect(() => {
     *     analytics.trackPageView(window.location.pathname)
     *   }, [])
     *   return null
     * })
     * ```
     */
    setTemplateScripts(scripts: React.ComponentType<any> | null): void {
        runInAction(() => {
            this.templateScripts = scripts
        })
        
        /** Логуємо для профайлера */
        this.logDebugEvent('setTemplateScripts', {
            scriptsName: scripts?.name || scripts?.displayName || 'anonymous',
            hasScripts: scripts !== null,
        })
    }

    /**
     * Встановлює функцію залежності
     */
    setDepFn(depFn: ((options: Record<string, any>) => boolean) | null): void {
        runInAction(() => {
            this.depFn = depFn
        })
    }

    /**
     * Встановлює функцію перевірки доступу
     */
    setAccessFn(accessFn: (options: Record<string, any>) => boolean): void {
        runInAction(() => {
            this.accessFn = accessFn
        })
    }

    /**
     * Встановлює поточний варіант відображення
     * Автоматично застосовує template, templateWrapper та props з variantItems
     *
     * @param variantKey - ключ варіанту з variantItems або null для скидання
     *
     * @example
     * ```typescript
     * uiManager.setVariantItems({
     *   table: { key: 'table', template: TableView },
     *   kanban: { key: 'kanban', template: KanbanView }
     * })
     * uiManager.setVariant('kanban') // Застосує KanbanView як template
     * uiManager.setVariant(null) // Скине всі налаштування варіанту
     * ```
     */
    setVariant(variantKey: string | null): void {
        /** Якщо передано null, скидаємо налаштування варіанту */
        if (variantKey === null) {
            runInAction(() => {
                this.variant = null
                this.template = null
                this.templateWrapper = null
                this.templateWrapperProps = {}
            })

            this.logDebugEvent('setVariant', { variant: null, action: 'reset' })
            this.updateDebugInfo()
            return
        }

        if (!variantKey) {
            console.warn('UIManager.setVariant: variantKey is empty')
            return
        }

        /** Якщо є конфігурація для цього варіанту, застосовуємо її */
        const variantConfig = this.variantItems[variantKey]

        if (!variantConfig) {
            console.warn(`UIManager.setVariant: variant "${variantKey}" not found in variantItems`)
            return
        }

        runInAction(() => {
            /** Спочатку встановлюємо всі властивості варіанту */

            /** Встановлюємо template з варіанту */
            if (variantConfig.template) {
                this.template = variantConfig.template
            }

            /** Встановлюємо templateWrapper якщо є */
            if (variantConfig.templateWrapper) {
                this.templateWrapper = variantConfig.templateWrapper
            }

            /** Об'єднуємо props з варіанту з існуючими */
            if (variantConfig.props) {
                this.props = { ...this.props, ...variantConfig.props }
            }

            /** Встановлюємо templateWrapperProps якщо є */
            if (variantConfig.templateWrapperProps) {
                this.templateWrapperProps = {
                    ...this.templateWrapperProps,
                    ...variantConfig.templateWrapperProps,
                }
            }

            /**
             * Встановлюємо variant в кінці, щоб ререндер відбувся
             * тільки після застосування всіх властивостей варіанту
             */
            this.variant = variantKey

            this.logDebugEvent('setVariant', {
                variant: variantKey,
                hasTemplate: !!variantConfig.template,
                hasWrapper: !!variantConfig.templateWrapper,
            })
        })

        this.updateDebugInfo()
    }

    /**
     * Встановлює доступні варіанти відображення
     *
     * @param items - об'єкт з конфігураціями варіантів
     *
     * @example
     * ```typescript
     * uiManager.setVariantItems({
     *   flexTree: {
     *     key: 'EntitreeFlexTree',
     *     title: 'Дерево компанії',
     *     icon: TbBinaryTree,
     *     template: EntitreeFlexTree
     *   },
     *   kanban: {
     *     key: 'kanban',
     *     title: 'Канбан',
     *     icon: TbLayoutKanban,
     *     template: KanbanOrdersMode
     *   }
     * })
     * ```
     */
    setVariantItems(items: Record<string, VariantItem>): void {
        this.variantItems = items
        this.logDebugEvent('setVariantItems', { count: Object.keys(items).length })
    }

    /**
     * Отримує поточний варіант відображення
     */
    getVariant(): string | null {
        return this.variant
    }

    /**
     * Отримує конфігурацію поточного варіанту
     */
    getCurrentVariantItem(): VariantItem | undefined {
        if (!this.variant) {
            return undefined
        }
        return this.variantItems[this.variant]
    }

    /**
     * Отримує всі доступні варіанти
     */
    getVariantItems(): Record<string, VariantItem> {
        return this.variantItems
    }

    /**
     * Перевіряє чи існує варіант
     */
    hasVariant(variantKey: string): boolean {
        return variantKey in this.variantItems
    }

    /**
     * Встановлює пропси для обгортки шаблону
     */
    setTemplateWrapperProps(props: ComponentProps): void {
        runInAction(() => {
            this.templateWrapperProps = { ...props }
        })
    }

    /**
     * Додає обробники подій
     */
    addEventsHandlers(eventsHandlers: EventHandlers): void {
        if (typeof eventsHandlers === 'object' && eventsHandlers !== null) {
            Object.keys(eventsHandlers).forEach((eventName) => {
                this.addEventHandlers(eventName, eventsHandlers[eventName])
            })
        }
    }

    /**
     * Додає обробники для конкретної події
     */
    addEventHandlers(eventName: string, eventHandlers: { [callbackName: string]: (...args: any[]) => any } = {}): void {
        Object.keys(eventHandlers).forEach((callbackName) => {
            const callbackFn = eventHandlers[callbackName]

            if (typeof callbackFn !== 'function') {
                return
            }

            this.eventManager.on(eventName, callbackName, callbackFn)
        })
    }

    /**
     * Видаляє обробник події
     */
    removeEventHandler(eventName: string, handlerName?: string): void {
        this.eventManager.off(eventName, handlerName)
    }

    /**
     * Об'єднує стани з існуючими
     */
    mergeStates(states: ComponentState): void {
        if (typeof states === 'object' && states !== null) {
            runInAction(() => {
                this.states = { ...this.states, ...states }
            })
        }
    }

    /**
     * Об'єднує сховища з існуючими (не observable)
     */
    mergeStorages(storages: ComponentStorage): void {
        if (typeof storages === 'object' && storages !== null) {
            this.storages = { ...this.storages, ...storages }
            this.logDebugEvent('mergeStorages', { count: Object.keys(storages).length })
        }
    }

    /**
     * Об'єднує налаштування з існуючими
     */
    mergeSettings(settings: ComponentSettings): void {
        if (typeof settings === 'object' && settings !== null) {
            runInAction(() => {
                this.settings = { ...this.settings, ...settings }
            })
        }
    }

    /**
     * Об'єднує пропси з існуючими
     */
    mergeProps(props: ComponentProps): void {
        if (typeof props === 'object' && props !== null) {
            this.props = { ...this.props, ...props }
            this.logDebugEvent('mergeProps', { count: Object.keys(props).length })
        }
    }

    /**
     * Встановлює всі пропси (не observable)
     */
    setProps(props: ComponentProps): void {
        if (typeof props === 'object' && props !== null) {
            this.props = { ...props }
            this.logDebugEvent('setProps', { count: Object.keys(props).length })
        }
    }

    /**
     * Встановлює окремий проп за шляхом (не observable)
     */
    setProp(path: string, value: any): void {
        if (typeof path === 'string' && path.length > 0) {
            SetTask(this.props, path, value)
            this.logDebugEvent('setProp', { path, valueType: typeof value })
        }
    }

    /**
     * Отримує проп за шляхом
     */
    getProp(path: string, defaultValue?: any): any {
        return GetTask(this.props, path, defaultValue)
    }

    /**
     * Очищує всі пропси (не observable)
     */
    clearProps(): void {
        this.props = {}
        this.logDebugEvent('clearProps')
    }

    /**
     * Об'єднує конфігурацію з поточним екземпляром
     */
    mergeToInstance({
        states = {},
        storages = {},
        settings = {},
        eventHandlers = {},
        template,
        templateError403,
        templateWrapper,
        templateScripts,
        templateWrapperProps,
        depFn,
        accessFn,
        children,
        props = {},
        variant,
        variantItems,
        ...restConfig
    }: Omit<IUIManagerConfig, 'context'> = {}): void {
        this.mergeStates(states)
        this.mergeStorages(storages)
        this.mergeSettings(settings)
        this.addEventsHandlers(eventHandlers)

        if (children) {
            this.children = children
        }

        if (template) {
            this.setTemplate(template)
        }

        if (templateWrapper) {
            this.setTemplateWrapper(templateWrapper)
        }

        if (templateError403) {
            this.setTemplateError403(templateError403)
        }

        if (templateScripts) {
            this.setTemplateScripts(templateScripts)
        }

        if (templateWrapperProps) {
            this.setTemplateWrapperProps(templateWrapperProps)
        }

        if (typeof depFn === 'function') {
            this.setDepFn(depFn)
        }

        /** Встановлюємо accessFn (якщо передано, інакше залишаємо значення за замовчуванням) */
        if (typeof accessFn === 'function') {
            this.setAccessFn(accessFn)
        }

        /** Встановлюємо варіанти перед встановленням поточного варіанту */
        if (variantItems) {
            this.setVariantItems(variantItems)
        }

        /** Встановлюємо поточний варіант (якщо є) */
        if (variant) {
            this.setVariant(variant)
        }

        /** Об'єднуємо props з конфігурації з іншими властивостями */
        this.mergeProps({ ...props, ...restConfig })
    }

    /**
     * Внутрішній метод рендерингу без обгортки в observer
     *
     * Логіка роботи:
     * 1. Перевіряємо accessFn - якщо повертає false → повертаємо templateError403 або null
     * 2. Якщо існує depFn і вона повертає false → повертаємо null
     * 3. Якщо відсутній template → повертаємо children
     * 4. Якщо присутній template → загортаємо children у template
     * 5. Якщо існує templateWrapper → загортаємо результат у templateWrapper
     * 6. Якщо існує templateScripts та виконані умови (accessFn + depFn) → додаємо скрипти у фрагмент
     *
     * Примітка: forceRenderCounter та variant використовуються для відстеження змін та реактивності
     */
    private renderInternal(): React.ReactElement | null {
        /** Читаємо forceRenderCounter для реактивності (не використовується, але викликає реакцію MobX) */
        void this.forceRenderCounter

        /** Читаємо variant для реактивності - при зміні варіанту компонент перерендериться */
        void this.variant

        /** Починаємо профілювання рендерингу */
        this.startRenderProfiling()

        /** Логуємо подію рендерингу */
        this.logDebugEvent('render', { timestamp: Date.now() })

        /** Перевіряємо функцію доступу (завжди існує зі значенням за замовчуванням) */
        const hasAccess = this.accessFn({
            accessMode: this.accessMode,
            accessItem: this.accessItem,
            [this.contextKey]: this
        })

        if (hasAccess === false) {
            this.logDebugEvent('render:access-denied')
            this.endRenderProfiling()

            /** Якщо є шаблон для помилки 403, відображаємо його */
            if (this.templateError403) {
                const Error403Component = this.templateError403
                const props = toJS(this.props)
                
                /** Фільтруємо context з props, щоб не передавати його в DOM */
                const { [this.contextKey]: _, ...filteredProps } = props

                let error403Result = React.createElement(Error403Component, filteredProps)

                /** Якщо наявний templateWrapper, загортаємо результат у нього */
                if (this.templateWrapper) {
                    const Wrapper = this.templateWrapper
                    error403Result = React.createElement(Wrapper, { ...this.templateWrapperProps }, error403Result)
                }

                return error403Result
            }

            return null
        }

        /** Перевіряємо функцію залежності */
        if (typeof this.depFn === 'function' && this.depFn({ [this.contextKey]: this }) === false) {
            this.endRenderProfiling()
            return null
        }

        let result: React.ReactElement | null = null

        /** Якщо відсутній template, повертаємо children як є */
        if (!this.template) {
            if (this.children) {
                result = this.children as React.ReactElement
            } else {
                result = null
            }
        } else {
            /** Якщо є template, загортаємо children у template */
            const Component = this.template
            const props = toJS(this.props)
            
            /** Фільтруємо context з props, щоб не передавати його в DOM */
            const { [this.contextKey]: _, ...filteredProps } = props
            
            result = React.createElement(Component, {
                ...filteredProps,
                children: this.children,
                [this.contextKey]: this.context,
            })
        }

        /** Якщо наявний templateWrapper, загортаємо результат у нього */
        if (this.templateWrapper && result) {
            const Wrapper = this.templateWrapper
            result = React.createElement(Wrapper, { ...this.templateWrapperProps }, result)
        }

        /** Рендеримо templateScripts якщо він наявний та виконані всі умови (depFn + accessFn) */
        if (this.templateScripts && result) {
            const ScriptsComponent = this.templateScripts
            const scriptsElement = React.createElement(ScriptsComponent, null)
            
            /** Створюємо фрагмент, що містить і скрипти, і основний результат */
            result = React.createElement(React.Fragment, null, scriptsElement, result)
        }

        /** Завершуємо профілювання */
        this.endRenderProfiling()
        return result
    }

    /**
     * Виконує рендеринг компонента на основі поточної конфігурації
     *
     * Якщо isObservable = true, автоматично обгортає результат у MobX observer.
     * Це дозволяє компоненту реагувати на зміни observable властивостей (states, settings).
     *
     * Якщо isObservable = false, повертає результат без обгортки observer.
     * Це корисно для оптимізації, коли компонент не потребує реактивності
     * або коли template містить великі об'єкти, які не потрібно відстежувати.
     *
     * @returns React елемент або null
     *
     * @example
     * ```tsx
     * // З observer (за замовчуванням)
     * const manager = new UIManager({ template: MyComponent })
     * return manager.render() // Обгорнуто в observer
     *
     * // Без observer
     * const manager = new UIManager({ template: MyComponent, isObservable: false })
     * return manager.render() // Не обгорнуто в observer
     *
     * // Примусовий перерендер після зміни template
     * manager.template = AnotherComponent
     * manager.forceRerender() // Викличе перерендер
     * ```
     */
    render(): React.ReactElement | null {
        if (this.isObservable) {
            /** Створюємо observer-обгортку для реактивного рендерингу */
            const ObserverWrapper = observer(() => this.renderInternal())
            return React.createElement(ObserverWrapper)
        } else {
            /** Рендеримо без observer для оптимізації */
            return this.renderInternal()
        }
    }

    /**
     * Примусово викликає перерендер компонента
     *
     * Використовується у випадках, коли потрібно оновити компонент без зміни observable властивостей.
     * Наприклад, після заміни не-observable template або при зміні зовнішніх залежностей.
     *
     * @example
     * ```typescript
     * // Замінюємо template і викликаємо перерендер
     * uiManager.template = NewComponent
     * uiManager.forceRerender()
     * ```
     */
    forceRerender(): void {
        runInAction(() => {
            this.forceRenderCounter++
        })

        this.logDebugEvent('forceRerender', { counter: this.forceRenderCounter })
        this.updateDebugInfo()
    }

    /**
     * Очищує всі дані компонента
     */
    clear(): void {
        /** Відписуємося від профілювання */
        if (this.debugConfig?.enabled) {
            profilerManager.unregisterComponent(this.debugConfig.id)
        }

        /** Очищуємо observable дані через MobX actions */
        this.clearStates()
        this.clearSettings()

        /** Очищуємо не-observable дані напряму */
        this.clearStorages()
        this.clearProps()

        runInAction(() => {
            this.templateWrapperProps = {}
            this.template = null
            this.templateWrapper = null
            this.templateError403 = null
            this.depFn = null
            this.accessFn = () => true
            this.variant = ''
        })

        /** Очищуємо варіанти */
        this.variantItems = {}

        /** Очищаємо всі обробники подій */
        this.eventManager.clear()
    }

    /** Методи для профілювання (лише у режимі розробки) */

    /**
     * Генерує унікальний ID для компонента
     */
    private generateId(): string {
        return `ui-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    }

    /**
     * Починає вимірювання часу рендеру
     */
    startRenderProfiling(): void {
        if (this.debugConfig?.enabled) {
            this.renderStartTime = performance.now()
        }
    }

    /**
     * Завершує вимірювання часу рендеру
     */
    endRenderProfiling(): void {
        if (this.debugConfig?.enabled && this.renderStartTime > 0) {
            const renderTime = performance.now() - this.renderStartTime
            profilerManager.updateComponent(this.debugConfig.id, renderTime)

            /** Якщо це шаблон, оновлюємо відповідні дані */
            if (this.debugConfig.type === 'template') {
                profilerManager.updateTemplateRender(this.debugConfig.id, renderTime)
            }

            this.renderStartTime = 0
        }
    }

    /**
     * Логує подію в профайлер
     */
    logDebugEvent(event: string, data?: any): void {
        if (this.debugConfig?.enabled) {
            profilerManager.addComponentEvent(this.debugConfig.id, event, data)
        }
    }

    /**
     * Встановлює статус компонента для профілювання
     */
    setDebugStatus(status: 'active' | 'inactive' | 'error', errorMessage?: string): void {
        if (this.debugConfig?.enabled) {
            profilerManager.setComponentStatus(this.debugConfig.id, status, errorMessage)
            this.logDebugEvent('setDebugStatus', { status, errorMessage })
        }
    }

    /**
     * Публічний метод для додавання події профілювання
     * Використовується компонентами для логування своїх подій
     *
     * @param event - назва події
     * @param data - додаткові дані події
     *
     * @example
     * ```typescript
     * listContext.addProfilerEvent('load:started', { itemsCount: 10 })
     * listContext.addProfilerEvent('filter:changed', { filterKeys: ['status', 'date'] })
     * ```
     */
    addProfilerEvent(event: string, data?: any): void {
        this.logDebugEvent(event, data)
    }

    /**
     * Публічний метод для встановлення статусу профілювання
     * Використовується компонентами для зміни свого статусу
     *
     * @param status - статус компонента ('active' | 'inactive' | 'error')
     * @param errorMessage - повідомлення про помилку (опціонально)
     *
     * @example
     * ```typescript
     * listContext.setProfilerStatus('active')
     * listContext.setProfilerStatus('error', 'Failed to load data')
     * listContext.setProfilerStatus('inactive')
     * ```
     */
    setProfilerStatus(status: 'active' | 'inactive' | 'error', errorMessage?: string): void {
        this.setDebugStatus(status, errorMessage)
    }

    /**
     * Оновлює інформацію профілювання
     */
    updateDebugInfo(): void {
        if (this.debugConfig?.enabled) {
            profilerManager.updateComponent(this.debugConfig.id)
        }
    }

    /**
     * Отримує ID для профілювання
     */
    getDebugId(): string | undefined {
        return this.debugConfig?.id
    }

    /**
     * Перевіряє, чи увімкнено профілювання
     */
    isProfilingEnabled(): boolean {
        return this.debugConfig?.enabled === true
    }

    /** =============== Розширені методи для роботи з даними =============== */

    /**
     * Batch оновлення states (оптимізовано для великих змін)
     */
    batchSetStates(updates: Array<{ path: string; value: any }>): void {
        if (!Array.isArray(updates) || updates.length === 0) return

        runInAction(() => {
            updates.forEach(({ path, value }) => {
                SetTask(this.states, path, value)
            })
        })

        this.logDebugEvent('batchSetStates', { count: updates.length })
        this.updateDebugInfo()
    }

    /**
     * Batch оновлення settings (оптимізовано для великих змін)
     */
    batchSetSettings(updates: Array<{ path: string; value: any }>): void {
        if (!Array.isArray(updates) || updates.length === 0) return

        runInAction(() => {
            updates.forEach(({ path, value }) => {
                SetTask(this.settings, path, value)
            })
        })

        this.logDebugEvent('batchSetSettings', { count: updates.length })
        this.updateDebugInfo()
    }

    /**
     * Підписка на зміни states за шляхом
     */
    watchState(path: string, callback: (newValue: any, oldValue: any) => void): () => void {
        let previousValue = this.getState(path)

        const eventName = `state:${path}:changed`
        const handlerName = `watch_${Date.now()}_${Math.random()}`

        this.eventManager.on(eventName, handlerName, () => {
            const newValue = this.getState(path)
            if (newValue !== previousValue) {
                callback(newValue, previousValue)
                previousValue = newValue
            }
        })

        // Повертаємо функцію для відписки
        return () => {
            this.eventManager.off(eventName, handlerName)
        }
    }

    /**
     * Підписка на зміни setting за шляхом
     */
    watchSetting(path: string, callback: (newValue: any, oldValue: any) => void): () => void {
        let previousValue = this.getSetting(path)

        const eventName = `setting:${path}:changed`
        const handlerName = `watch_${Date.now()}_${Math.random()}`

        this.eventManager.on(eventName, handlerName, () => {
            const newValue = this.getSetting(path)
            if (newValue !== previousValue) {
                callback(newValue, previousValue)
                previousValue = newValue
            }
        })

        // Повертаємо функцію для відписки
        return () => {
            this.eventManager.off(eventName, handlerName)
        }
    }

    /**
     * Валідує дані перед встановленням
     */
    validateState(path: string, value: any, validator: (value: any) => boolean): boolean {
        try {
            const isValid = validator(value)
            if (!isValid) {
                this.logDebugEvent('validateState:failed', { path, value })
                this.setDebugStatus('error', `Validation failed for state: ${path}`)
            }
            return isValid
        } catch (error) {
            this.logDebugEvent('validateState:error', { path, error })
            this.setDebugStatus('error', `Validation error for state: ${path}`)
            return false
        }
    }

    /**
     * Встановлює state з валідацією
     */
    setStateWithValidation(path: string, value: any, validator: (value: any) => boolean): boolean {
        if (this.validateState(path, value, validator)) {
            this.setState(path, value)
            return true
        }
        return false
    }

    /**
     * Отримує snapshot всіх даних компонента
     */
    getSnapshot(): {
        states: ComponentState
        settings: ComponentSettings
        storages: ComponentStorage
        props: ComponentProps
        timestamp: number
    } {
        return {
            states: toJS(this.states),
            settings: toJS(this.settings),
            storages: { ...this.storages },
            props: { ...this.props },
            timestamp: Date.now(),
        }
    }

    /**
     * Відновлює дані з snapshot
     */
    restoreFromSnapshot(snapshot: {
        states?: ComponentState
        settings?: ComponentSettings
        storages?: ComponentStorage
        props?: ComponentProps
    }): void {
        if (snapshot.states) {
            this.setStates(snapshot.states)
        }
        if (snapshot.settings) {
            this.setSettings(snapshot.settings)
        }
        if (snapshot.storages) {
            this.setStorages(snapshot.storages)
        }
        if (snapshot.props) {
            this.setProps(snapshot.props)
        }

        this.logDebugEvent('restoreFromSnapshot')
    }

    /**
     * Перевіряє чи є зміни у states
     */
    hasStateChanges(path: string, expectedValue: any): boolean {
        const currentValue = this.getState(path)
        return currentValue !== expectedValue
    }

    /**
     * Отримує статистику компонента
     */
    getComponentStats(): {
        statesCount: number
        settingsCount: number
        storagesCount: number
        propsCount: number
        eventHandlersCount: number
        childrenCount: number
    } {
        return {
            statesCount: Object.keys(this.states).length,
            settingsCount: Object.keys(this.settings).length,
            storagesCount: Object.keys(this.storages).length,
            propsCount: Object.keys(this.props).length,
            eventHandlersCount: this.eventManager.getEventNames?.().length || 0,
            childrenCount: this.children ? 1 : 0,
        }
    }

    /**
     * Логує зміну даних для відстеження
     */
    private logDataChange(type: 'state' | 'setting' | 'storage' | 'prop', path: string, value: any): void {
        if (this.debugConfig?.enabled) {
            this.logDebugEvent(`${type}:changed`, {
                path,
                valueType: typeof value,
                timestamp: Date.now(),
            })
        }
    }

    /**
     * Перевіряє чи перебуваємо ми в режимі розробки
     */
    private isDevMode(): boolean {
        const isClient = typeof window !== 'undefined'
        const env = (import.meta as any).env
        return (
            env?.DEV === true || env?.MODE === 'development' || (isClient && window.location.hostname === 'localhost')
        )
    }

    /**
     * Отримує доступ до EventManager
     */
    getEventManager(): EventManager {
        return this.eventManager
    }

    /**
     * Отримує контекст компонента
     */
    getContext(): any {
        return this.context
    }

    /**
     * Встановлює новий контекст
     */
    setContext(context: any): void {
        this.context = context
    }
}

/**
 * Фабрична функція для створення екземпляра UIManager
 */
export function createUIManager(config: IUIManagerConfig = {}): UIManager {
    return new UIManager(config)
}

/** Експорт за замовчуванням */
export default UIManager
